import { memo, type ReactNode } from 'react';
import type { ContentBlock, TopicContent, Sloka, DataTable, DataTableRow } from '../types';
import ContentToggle from './ContentToggle';
import MermaidDiagram from './MermaidDiagram';
import ZoomableSvgDiagram from './ZoomableSvgDiagram';

function isSectionRow(row: DataTableRow): row is { sectionLabel: string } {
    return !Array.isArray(row);
}

interface TopicBlocksProps {
    content: TopicContent;
}

function formatSloka(sloka: string): string[] {
    if (sloka.includes('\n')) {
        return sloka
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
    }

    const parts = sloka
        .split('|')
        .map((part) => part.trim())
        .filter(Boolean);

    const lines: string[] = [];
    parts.forEach((part) => {
        if (part.length > 50) {
            const words = part.split(/\s+/);
            let currentLine = '';

            words.forEach((word) => {
                if ((currentLine + ' ' + word).length > 50 && currentLine) {
                    lines.push(currentLine.trim());
                    currentLine = word;
                } else {
                    currentLine = currentLine ? `${currentLine} ${word}` : word;
                }
            });

            if (currentLine) {
                lines.push(currentLine.trim());
            }
        } else {
            lines.push(part);
        }
    });

    return lines;
}

function normalizeReference(reference: string): string {
    let normalized = reference;
    normalized = normalized.replace(/bhagavad[-\s]gītā/gi, 'Bhagavad-Gītā');
    normalized = normalized.replace(/bhagavad[-\s]gita/gi, 'Bhagavad-Gītā');
    normalized = normalized.replace(/\bsrimad\b/gi, 'Śrīmad');
    normalized = normalized.replace(/\bbhagavatam\b/gi, 'Bhāgavatam');
    return normalized;
}

function highlightCitations(text: string): string {
    return text;
}

function SlokaDisplay({ sloka }: { sloka: Sloka }) {
    const slokaLines = formatSloka(sloka.sloka);
    const normalizedReference = normalizeReference(sloka.reference);

    return (
        <div className="my-6 rounded-r-lg border-l-4 border-darkBlue bg-gray-50 p-6 dark:border-lightBlue dark:bg-gray-800/30">
            <div
                className="mb-4 text-center text-base font-medium tracking-wide text-darkBlue dark:text-lightBlue"
                style={{ fontSize: '19px' }}
            >
                {normalizedReference}
            </div>
            <div className="mb-4 text-center font-serif italic leading-relaxed text-black dark:text-gray-100" style={{ fontSize: '19px' }}>
                {slokaLines.map((line, idx) => (
                    <div key={idx} className="text-center">
                        {line}
                    </div>
                ))}
            </div>
            <div
                className="whitespace-pre-line text-center text-base italic text-black dark:text-gray-100"
                style={{ fontSize: '19px', lineHeight: 1.75 }}
                dangerouslySetInnerHTML={{ __html: highlightCitations(sloka.translation) }}
            />
        </div>
    );
}

function TableDisplay({ table }: { table: DataTable }) {
    return (
        <div className="my-6 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full border-collapse md:table">
                {table.label && (
                    <caption className="p-4 pb-0 text-center text-sm font-bold uppercase tracking-wide text-lightOrange">
                        {table.label}
                    </caption>
                )}
                <thead className="hidden md:table-header-group">
                    <tr className="bg-darkBlue text-white dark:bg-gray-800">
                        {table.headers.map((header) => (
                            <th key={header} className="p-4 text-left font-bold" style={{ fontSize: '17px' }}>
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="block md:table-row-group">
                    {table.rows.map((row, rowIndex) => {
                        if (isSectionRow(row)) {
                            return (
                                <tr key={row.sectionLabel} className="block md:table-row">
                                    <td
                                        colSpan={table.headers.length}
                                        className="block bg-gray-50 p-3 text-center text-sm font-bold uppercase tracking-wide text-lightOrange md:table-cell dark:bg-gray-800/30"
                                    >
                                        {row.sectionLabel}
                                    </td>
                                </tr>
                            );
                        }

                        return (
                            <tr
                                key={row[0] ?? rowIndex}
                                className="block border-b border-gray-200 p-4 last:border-b-0 md:table-row md:border-b md:p-0 odd:md:bg-gray-50 dark:border-gray-700 dark:odd:md:bg-gray-800/30"
                            >
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={table.headers[cellIndex] ?? cellIndex}
                                        className="mb-3 block leading-relaxed text-black last:mb-0 md:table-cell md:p-4 md:align-top dark:text-white [&_strong]:text-[#A62B1F]/90 dark:[&_strong]:text-[#E07B6A]"
                                        style={{ fontSize: '17px' }}
                                    >
                                        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-lightOrange md:hidden">
                                            {table.headers[cellIndex]}
                                        </span>
                                        <span
                                            className={cellIndex === 0 ? 'font-bold text-darkBlue dark:text-lightBlue' : ''}
                                            dangerouslySetInnerHTML={{ __html: cell }}
                                        />
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function renderBlock(block: ContentBlock, key: number | string): ReactNode {
    if (block.type === 'text') {
        return (
            <div
                key={key}
                className="leading-relaxed text-black dark:text-gray-100 [&_strong]:text-[#A62B1F]/90 dark:[&_strong]:text-[#E07B6A]"
                style={{ fontSize: '19px' }}
                dangerouslySetInnerHTML={{ __html: block.content || '' }}
            />
        );
    }

    if (block.type === 'sloka' && block.sloka) {
        return <SlokaDisplay key={key} sloka={block.sloka} />;
    }

    if (block.type === 'heading' && block.headingText) {
        return (
            <div key={key} className="mb-4 mt-6">
                <h3
                    className="mb-3 text-xl font-bold text-darkBlue dark:text-lightBlue"
                    style={{ fontSize: '19px', lineHeight: 1.5 }}
                >
                    {block.headingText}
                </h3>
                {block.content && (
                    <div
                        className="leading-relaxed text-black dark:text-gray-100 [&_strong]:text-[#A62B1F]/90 dark:[&_strong]:text-[#E07B6A]"
                        style={{ fontSize: '19px' }}
                        dangerouslySetInnerHTML={{ __html: block.content }}
                    />
                )}
            </div>
        );
    }

    if (block.type === 'sub-heading' && block.headingText) {
        return (
            <div key={key} className="mb-4 mt-6">
                <h4
                    className="mb-3 text-center font-bold text-darkBlue dark:text-lightBlue"
                    style={{ fontSize: '17px', lineHeight: 1.5 }}
                >
                    {block.headingText}
                </h4>
                {block.content && (
                    <div
                        className="text-center leading-relaxed text-black dark:text-gray-100 [&_strong]:text-[#A62B1F]/90 dark:[&_strong]:text-[#E07B6A]"
                        style={{ fontSize: '19px' }}
                        dangerouslySetInnerHTML={{ __html: block.content }}
                    />
                )}
            </div>
        );
    }

    if (block.type === 'dialogue' && (block.objection || block.response)) {
        return (
            <div key={key} className="my-6">
                {block.objection && (
                    <div className="mb-3">
                        {block.showObjectionLabel !== false && (
                            <span className="font-bold text-lightOrange" style={{ fontSize: '19px' }}>
                                Purvapaksa: (Objection)
                            </span>
                        )}
                        <div
                            className={`leading-relaxed italic text-black dark:text-gray-100 [&_strong]:text-[#A62B1F]/90 dark:[&_strong]:text-[#E07B6A] ${
                                block.showObjectionLabel !== false ? 'mt-2' : ''
                            }`}
                            style={{ fontSize: '19px' }}
                            dangerouslySetInnerHTML={{ __html: block.objection }}
                        />
                    </div>
                )}
                {block.response && (
                    <div className="mt-4">
                        <span className="font-bold text-lightOrange" style={{ fontSize: '19px' }}>
                            Uttarapaksa: (Response)
                        </span>
                        <div
                            className="mt-2 leading-relaxed italic text-black dark:text-gray-100 [&_p+_p]:mt-4 [&_strong]:text-[#A62B1F]/90 dark:[&_strong]:text-[#E07B6A]"
                            style={{ fontSize: '19px' }}
                            dangerouslySetInnerHTML={{
                                __html: highlightCitations(block.response),
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }

    if (block.type === 'diagram' && block.diagram) {
        return block.diagram.svgMarkup ? (
            <ZoomableSvgDiagram
                key={key}
                svgMarkup={block.diagram.svgMarkup}
                caption={block.diagram.caption}
                zoomable={block.diagram.zoomable}
            />
        ) : (
            <MermaidDiagram key={key} mermaidCode={block.diagram.mermaidCode ?? ''} caption={block.diagram.caption} />
        );
    }

    if (block.type === 'table' && block.table) {
        return <TableDisplay key={key} table={block.table} />;
    }

    if (block.type === 'toggle' && block.toggle) {
        return (
            <ContentToggle
                key={key}
                toggle={block.toggle}
                renderBlocks={(blocks) => blocks.map((nestedBlock, nestedIndex) => renderBlock(nestedBlock, nestedIndex))}
            />
        );
    }

    if (block.type === 'divider') {
        const isAccent = block.dividerEmphasis === 'accent';
        return (
            <div key={key} className="my-8 flex items-center gap-3">
                <div className="h-0 flex-1 border-t-2 border-gray-300 dark:border-gray-700" />
                {block.dividerLabel && (
                    <span
                        className={
                            isAccent
                                ? 'text-sm font-bold uppercase tracking-wide text-lightOrange'
                                : 'text-xs font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500'
                        }
                    >
                        {block.dividerLabel}
                    </span>
                )}
                <div className="h-0 flex-1 border-t-2 border-gray-300 dark:border-gray-700" />
            </div>
        );
    }

    return null;
}

function TopicBlocks({ content }: TopicBlocksProps) {
    return (
        <>
            {content.primarySloka && (
                <>
                    <div className="mb-8">
                        <div className="font-bold text-lightOrange" style={{ fontSize: '19px', lineHeight: 1.5 }}>
                            Primary Sloka(s) -{' '}
                            {content.primarySlokaHeader
                                ? content.primarySlokaHeader
                                : Array.isArray(content.primarySloka)
                                  ? content.primarySloka.map((sloka) => normalizeReference(sloka.reference)).join(', ')
                                  : normalizeReference(content.primarySloka.reference)}
                        </div>
                    </div>

                    {Array.isArray(content.primarySloka) ? (
                        content.primarySloka.map((sloka, idx) => <SlokaDisplay key={idx} sloka={sloka} />)
                    ) : (
                        <SlokaDisplay sloka={content.primarySloka} />
                    )}
                </>
            )}

            <div className="space-y-6">{content.blocks.map((block, index) => renderBlock(block, index))}</div>
        </>
    );
}

export default memo(TopicBlocks);
